/// <reference lib="dom" />

/**
 * worldParallax.ts
 * Per-world parallax-image picker.
 *
 * Resolves the (far, mid, near) parallax triple for a given {@link WorldId}
 * from a loaded {@link RenderAssets} bundle. Worlds without bespoke art
 * fall back to the World 1 Sunmeadow palette so an unfinished world still
 * renders something rather than a black sky.
 *
 * @module worldParallax
 */

import type { WorldId } from '../../constants/gameConstants';
import type { RenderAssets } from './RenderAssets';

/** A resolved parallax layer triple. */
export interface ParallaxTriple {
  far: HTMLImageElement;
  mid: HTMLImageElement;
  near: HTMLImageElement;
}

/**
 * Pick the parallax layer triple for `worldId` from `assets`.
 *
 * @param worldId - World currently being rendered.
 * @param assets  - Loaded asset bundle.
 * @returns The world-specific triple, or the W1 Sunmeadow fallback.
 */
export function pickParallax(worldId: WorldId, assets: RenderAssets): ParallaxTriple {
  if (worldId === 3) {
    return {
      far: assets.world3BgFarImg,
      mid: assets.world3BgMidImg,
      near: assets.world3BgNearImg,
    };
  }
  if (worldId === 4) {
    return {
      far: assets.world4BgFarImg,
      mid: assets.world4BgMidImg,
      near: assets.world4BgNearImg,
    };
  }
  if (worldId === 5) {
    return {
      far: assets.world5BgFarImg,
      mid: assets.world5BgMidImg,
      near: assets.world5BgNearImg,
    };
  }
  // DECISION: W2 has no platforming parallax (the space shmup uses its own
  // background system) and falls back to W1's Sunmeadow art.
  return {
    far: assets.bgFarImg,
    mid: assets.bgMidImg,
    near: assets.bgNearImg,
  };
}
